import type { ReactNode } from 'react';
import { useState } from 'react';
import type { Build, BuildEvent } from '@/api/schemas';
import { formatDateTime, formatDuration, humanize, shortSha, timeAgo } from '@/lib/format';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { EnvPill, PlatformIcon, Tag } from '@/components/badges';
import { ProviderBadge } from '@/components/ProviderBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useSeesProviders, useWording } from '@/lib/useAudience';
import { buildDuration, requesterOf } from '../buildHelpers';
import { executionModeLabel, serviceName, sourceLabel } from '../buildLabels';
import { providerShortName } from './headerModel';
import { advancedRows, deadlineText, easProfileOf, endRowLabel, estimatedCostText, runnerLineOf, startedAtOf, toolchainText, versionRowLabel } from './summaryModel';

/** One metadata pair. `wide` spans both columns for values that do not fit half of the 380 px card; `wrap` lets a sentence wrap. */
function Row({ label, children, mono = true, wide = false, wrap = false }: { label: string; children: ReactNode; mono?: boolean; wide?: boolean; wrap?: boolean }) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-0.5 border-b py-2 last:border-b-0 sm:border-b-0 sm:py-0', wide && 'sm:col-span-2')}>
      <dt className="text-xs text-subtle">{label}</dt>
      <dd className={cn('text-[13px] text-foreground', mono && 'font-mono', wrap ? '[overflow-wrap:anywhere]' : 'truncate')}>{children}</dd>
    </div>
  );
}

/**
 * Build metadata card (screen 03 right column): app, profile, env, platform, how it is built, git, version, requester,
 * timings. Platform operators also get the build service row and its raw data in a collapsed "Advanced details";
 * clients never see the provider behind Hangar (SPEC decision 23).
 */
export function BuildSummary({ build: b, now, events }: { build: Build; now: number; events?: BuildEvent[] }) {
  const operator = useSeesProviders();
  return (
    <Card className="enter" data-testid="build-summary">
      <CardHeader>
        <CardTitle>
          <Kicker>Build details</Kicker>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-x-6 sm:grid-cols-2 sm:gap-y-3">
          <IdentityRows b={b} />
          {operator ? <ProviderRow b={b} /> : null}
          <SourceRows b={b} />
          <TimingRows b={b} now={now} events={events} />
          <OptionalRows b={b} />
        </dl>
        {b.error ? <ErrorBox error={b.error} /> : null}
        {operator ? <AdvancedDetails b={b} /> : null}
      </CardContent>
    </Card>
  );
}

function IdentityRows({ b }: { b: Build }) {
  const easProfile = easProfileOf(b);
  return (
    <>
      <Row label="App" wide>
        {b.app.slug} <span className="text-subtle">· {b.app.name}</span>
      </Row>
      <Row label="Profile">
        {b.profile.slug}
        {easProfile ? <span className="text-subtle"> → {easProfile}</span> : null}
      </Row>
      <Row label="Environment" mono={false}>
        <EnvPill env={b.environment} />
      </Row>
      <Row label="Platform" mono={false}>
        <PlatformIcon platform={b.platform} withLabel />
      </Row>
      <Row label="How it's built" mono={false}>
        {executionModeLabel(b.executionMode)}
      </Row>
    </>
  );
}

/** The build service and account (operators); a link to the build there once it exists (never a "not attached" placeholder). */
function ProviderRow({ b }: { b: Build }) {
  return (
    <Row label="Build service" mono={false} wide>
      <span className="flex flex-wrap items-center gap-1.5">
        <ProviderBadge kind={b.providerAccount.kind} />
        <span className="truncate font-mono text-[13px]">{providerShortName(b.providerAccount.name)}</span>
        {b.provider?.buildUrl ? <ProviderBuildLink url={b.provider.buildUrl} kind={b.providerAccount.kind} /> : null}
      </span>
    </Row>
  );
}

function ProviderBuildLink({ url, kind }: { url: string; kind: string }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="build-summary-provider-link" className="inline-flex items-center gap-1 rounded-sm text-[13px] text-foreground underline-offset-4 hover:underline">
      View on {serviceName(kind)}
      <Icon name="open_in_new" size={14} />
    </a>
  );
}

function SourceRows({ b }: { b: Build }) {
  const req = requesterOf(b);
  return (
    <>
      <Row label="Branch · commit">
        {b.git.branch ?? '—'} <span className="text-subtle">· {shortSha(b.git.commitSha) || '—'}</span>
      </Row>
      <Row label={versionRowLabel(b.platform)}>
        {b.versionName ?? '—'} <span className="text-subtle">· {b.buildNumber ?? '—'}</span>
      </Row>
      <Row label="Requested by" mono={false} wide>
        <span className="flex min-w-0 items-center gap-1.5">
          <Icon name={req.icon} size={16} className="shrink-0 text-subtle" />
          <span className="truncate font-mono text-[13px]">{req.label}</span>
          <Tag>{sourceLabel(b.source)}</Tag>
        </span>
      </Row>
      <RunnerRow b={b} />
      <Row label="Priority">
        {b.priority}
        {b.queuePosition ? <span className="text-subtle"> · queue #{b.queuePosition}</span> : null}
      </Row>
    </>
  );
}

/** `hangar_runner`: Hangar's own runner started the build (no CI). */
function RunnerRow({ b }: { b: Build }) {
  const line = runnerLineOf(b);
  if (!line) return null;
  return (
    <Row label="What Hangar does" mono={false} wide wrap>
      <span className="flex min-w-0 items-start gap-1.5" data-testid="build-runner">
        <Icon name="bolt" size={16} className="mt-0.5 shrink-0 text-subtle" />
        <span className="text-[13px] [overflow-wrap:anywhere]">{line}</span>
      </span>
    </Row>
  );
}

/** A timestamp as "5 min ago", the exact time on hover. */
const Ago = ({ iso, now }: { iso: string; now: number }) => <span title={formatDateTime(iso)}>{timeAgo(iso, now)}</span>;

function TimingRows({ b, now, events }: { b: Build; now: number; events?: BuildEvent[] }) {
  const startedAt = startedAtOf(b, events);
  return (
    <>
      <Row label="Duration">{formatDuration(buildDuration(b, now))}</Row>
      <Row label="Created">
        <Ago iso={b.createdAt} now={now} />
      </Row>
      <Row label="Started">{startedAt ? <Ago iso={startedAt} now={now} /> : 'Not yet'}</Row>
      <Row label={endRowLabel(b)}>
        <EndValue b={b} now={now} />
      </Row>
    </>
  );
}

/** When it finished, else how long it has left before Hangar stops it (the exact time on hover), else a dash. */
function EndValue({ b, now }: { b: Build; now: number }) {
  if (b.finishedAt) return <Ago iso={b.finishedAt} now={now} />;
  return b.deadlineAt ? <span title={`Stops at ${formatDateTime(b.deadlineAt)}`}>{deadlineText(b.deadlineAt, now)}</span> : <>—</>;
}

/** Rows that only exist for some builds: cost, toolchain, native fingerprint, OTA channel, parent build. */
function OptionalRows({ b }: { b: Build }) {
  const cost = estimatedCostText(b);
  const toolchain = toolchainText(b);
  return (
    <>
      {cost !== null ? <Row label="Estimated cost">USD {cost}</Row> : null}
      {toolchain !== null ? (
        <Row label="Toolchain" wide>
          {toolchain}
        </Row>
      ) : null}
      {b.fingerprint ? (
        <Row label="Native fingerprint">
          <span title={b.fingerprint}>{b.fingerprint.slice(0, 12)}</span>
        </Row>
      ) : null}
      {b.updateChannel ? <Row label="OTA channel">{b.updateChannel}</Row> : null}
      {b.parentBuildId ? <ParentRow b={b} parentBuildId={b.parentBuildId} /> : null}
    </>
  );
}

function ParentRow({ b, parentBuildId }: { b: Build; parentBuildId: string }) {
  return (
    <Row label="Parent build">
      <span title={parentBuildId}>{parentBuildId.slice(0, 8)}</span>
      {b.redeployMode ? <span className="text-subtle"> · {humanize(b.redeployMode)}</span> : null}
    </Row>
  );
}

function ErrorBox({ error }: { error: NonNullable<Build['error']> }) {
  const say = useWording();
  return (
    <div data-testid="build-error" className="mt-4 flex items-start gap-2 rounded-lg border border-failed/30 bg-failed/10 px-3 py-2 text-[13px] text-failed">
      <Icon name="error" size={18} className="mt-px shrink-0" />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <code className="font-mono text-[11px]">{error.code}</code>
          <Tag className="text-failed">{error.category}</Tag>
        </div>
        <p className="mt-0.5 break-words">{say(error.message)}</p>
      </div>
    </div>
  );
}

/** The build service's own data, for whoever debugs a build: collapsed by default (registry Collapsible). */
function AdvancedDetails({ b }: { b: Build }) {
  const [open, setOpen] = useState(false);
  const rows = advancedRows(b);
  if (rows.length === 0) return null;
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-4 border-t pt-2" data-testid="build-advanced">
      <CollapsibleTrigger className="-mx-1 flex min-h-11 w-[calc(100%+0.5rem)] items-center gap-1.5 rounded-md px-1 text-left text-[13px] font-medium text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:min-h-9">
        <Icon name="expand_more" size={18} className={cn('shrink-0 transition-transform duration-150', open && 'rotate-180')} />
        Advanced details
      </CollapsibleTrigger>
      <CollapsibleContent>
        <dl className="mt-1 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 font-mono text-xs">
          {rows.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-subtle">{k}</dt>
              <dd className="break-all text-muted-foreground">{v}</dd>
            </div>
          ))}
        </dl>
      </CollapsibleContent>
    </Collapsible>
  );
}
